import { useMemo, useState } from 'react';
import { BookOpen, CheckCircle2, Clipboard, Code2, Search, ShieldCheck, Terminal } from 'lucide-react';

type ApiGroup = 'system' | 'mailbox' | 'platform' | 'compat';
type AuthMode = 'public' | 'apiKey' | 'admin' | 'lease';
type SnippetLanguage = 'powershell' | 'curl';

interface ApiField {
  name: string;
  location: 'Path' | 'Query' | 'Body' | 'Header';
  type: string;
  required: boolean;
  description: string;
}

interface ApiEndpoint {
  id: string;
  group: ApiGroup;
  method: 'GET' | 'POST';
  path: string;
  examplePath?: string;
  title: string;
  description: string;
  auth: AuthMode;
  fields: ApiField[];
  requestExample?: string;
  responseExample: string;
  statuses: string[];
}

const groupOptions: Array<{ key: 'all' | ApiGroup; label: string }> = [
  { key: 'all', label: '全部' },
  { key: 'mailbox', label: '邮箱池' },
  { key: 'platform', label: '平台注册' },
  { key: 'compat', label: '临时地址兼容' },
  { key: 'system', label: '系统' },
];

const endpoints: ApiEndpoint[] = [
  {
    id: 'health', group: 'system', method: 'GET', path: '/api/health', title: '服务健康检查',
    description: '供反向代理、容器编排和外部监控确认 smartMail 服务是否可用。', auth: 'public', fields: [],
    responseExample: `{
  "ok": true,
  "message": "ok",
  "data": { "status": "ok", "name": "smartMail", "time": "2026-07-19T12:00:00.000Z" }
}`,
    statuses: ['200 服务正常'],
  },
  {
    id: 'api-key-list', group: 'system', method: 'GET', path: '/api/api-keys', title: '查询权限密钥',
    description: '返回权限密钥元数据和 recoverable 状态，不在列表响应中暴露完整明文。新创建的密钥会加密留存；升级前创建的历史密钥 recoverable=false。', auth: 'apiKey', fields: [],
    responseExample: `{
  "ok": true,
  "data": [{
    "id": 8,
    "name": "第三方服务",
    "prefix": "sm_abcd123",
    "recoverable": true,
    "scopes": ["mail:read", "mail:reserve"],
    "enabled": true,
    "lastUsedAt": null,
    "createdAt": "2026-08-13T10:00:00.000Z"
  }]
}`,
    statuses: ['200 查询成功', '401 密钥无效'],
  },
  {
    id: 'api-key-create', group: 'system', method: 'POST', path: '/api/api-keys', title: '创建权限密钥',
    description: '创建独立 API Key。名称会自动去除首尾空格；服务端保存 SHA-256 鉴权摘要和 AES-256-GCM 密文，创建后仍可由管理员读取并复制。', auth: 'apiKey',
    fields: [
      { name: 'name', location: 'Body', type: 'string', required: true, description: '非空密钥名称' },
      { name: 'scopes', location: 'Body', type: 'string[]', required: false, description: '权限范围数组；省略时使用默认范围' },
    ],
    requestExample: `{
  "name": "第三方服务",
  "scopes": ["mail:read", "mail:reserve", "mail:report", "platform:report"]
}`,
    responseExample: `{
  "ok": true,
  "data": {
    "id": 8,
    "key": "sm_example_generated_key",
    "prefix": "sm_exampl"
  }
}`,
    statuses: ['201 创建成功', '400 名称为空或参数无效', '401 密钥无效'],
  },
  {
    id: 'api-key-reveal', group: 'system', method: 'GET', path: '/api/api-keys/{id}/reveal',
    examplePath: '/api/api-keys/8/reveal', title: '读取并复制已创建密钥',
    description: '使用服务器管理员密钥解密指定 API Key，响应带 Cache-Control: no-store。升级前创建且没有可恢复密文的历史密钥会返回 409，需要重新创建。', auth: 'admin',
    fields: [
      { name: 'id', location: 'Path', type: 'number', required: true, description: '权限密钥主键' },
      { name: 'Authorization', location: 'Header', type: 'Bearer token', required: true, description: 'Bearer + SMARTMAIL_ADMIN_KEY' },
    ],
    responseExample: `{
  "ok": true,
  "data": { "key": "sm_example_generated_key" }
}`,
    statuses: ['200 读取成功', '401 未认证', '403 不是管理员密钥', '404 密钥不存在', '409 历史密钥不可恢复或密文校验失败'],
  },
  {
    id: 'icloud-health-v1', group: 'system', method: 'GET', path: '/api/v1/health', title: 'iCloud 链路健康检查',
    description: '兼容 iCloud-Privacy-Mail 健康契约，检查 smartMail API 和已配置的 iCloud pickup/IMAP 凭据是否可用。', auth: 'apiKey', fields: [],
    responseExample: `{
  "success": true,
  "service": "smartmail",
  "api_active": true,
  "auth_required": true,
  "icloud_active": true,
  "time": "2026-08-02T12:00:00.000Z"
}` ,
    statuses: ['200 服务正常', '401 密钥无效'],
  },
  {
    id: 'mailbox-list', group: 'mailbox', method: 'GET', path: '/api/mailboxes',
    examplePath: '/api/mailboxes?page_size=20&category=safe', title: '查询邮箱列表',
    description: '按搜索词、分类、健康状态和提供商分页查询启用的导入邮箱（不包含域名邮箱与失效邮箱）。大数据量接入建议使用 cursor 游标。', auth: 'apiKey',
    fields: [
      { name: 'search', location: 'Query', type: 'string', required: false, description: '邮箱或备注搜索词' },
      { name: 'category', location: 'Query', type: 'string', required: false, description: '邮箱分类，例如 safe' },
      { name: 'health', location: 'Query', type: 'string', required: false, description: '健康状态，例如 healthy' },
      { name: 'provider', location: 'Query', type: 'string', required: false, description: '邮箱提供商，例如 outlook' },
      { name: 'page_size', location: 'Query', type: 'number', required: false, description: '单页数量，默认 20' },
      { name: 'cursor', location: 'Query', type: 'number', required: false, description: '上一页返回的 nextCursor' },
    ],
    responseExample: `{
  "ok": true,
  "data": {
    "items": [{ "id": 1, "email": "user@outlook.com", "healthStatus": "healthy" }],
    "pagination": { "total": 100, "nextCursor": 1, "hasMore": true }
  }
}`,
    statuses: ['200 查询成功', '401 密钥无效'],
  },
  {
    id: 'mailbox-import', group: 'mailbox', method: 'POST', path: '/api/mailboxes/import', title: '批量导入邮箱',
    description: '以结构化数组导入邮箱；相同邮箱会幂等更新，不会重复创建。', auth: 'apiKey',
    fields: [
      { name: 'items', location: 'Body', type: 'array', required: true, description: '邮箱对象数组' },
      { name: 'items[].email', location: 'Body', type: 'string', required: true, description: '邮箱地址' },
      { name: 'items[].password', location: 'Body', type: 'string', required: false, description: '邮箱密码；iCloud pickup 模式可留空，服务端会从 clientId 链接解析 KEY；IMAP 模式填写 Apple App 专用密码' },
      { name: 'items[].clientId', location: 'Body', type: 'string', required: false, description: 'OAuth Client ID；iCloud pickup 模式填写完整收码链接（fragment 的 KEY 会自动解析），IMAP 模式填写真实 iCloud 登录邮箱' },
      { name: 'items[].refreshToken', location: 'Body', type: 'string', required: false, description: 'OAuth Refresh Token；iCloud 可留空' },
      { name: 'items[].provider', location: 'Body', type: 'string', required: false, description: '显式提供商：outlook、icloud、gmail 或 other' },
      { name: 'defaultCategory', location: 'Body', type: 'string', required: false, description: '默认分类，默认 safe' },
    ],
    requestExample: `{
  "defaultCategory": "safe",
  "items": [{
    "email": "alias@privaterelay.appleid.com",
    "provider": "icloud",
    "password": "xxxx-xxxx-xxxx-xxxx",
    "clientId": "owner@icloud.com",
    "refreshToken": ""
  }]
}`,
    responseExample: `{
  "ok": true,
  "data": { "imported": 1, "updated": 0, "skipped": 0 }
}`,
    statuses: ['200 导入完成', '400 参数或格式错误', '401 密钥无效'],
  },
  {
    id: 'mailbox-reserve', group: 'mailbox', method: 'POST', path: '/api/mailboxes/reserve', title: '领取可用邮箱',
    description: '为指定平台并发安全地领取邮箱。按种类独立复用；依次领取导入邮箱、现有域名邮箱，耗尽后随机选择 active 域名生成临时邮箱。', auth: 'apiKey',
    fields: [
      { name: 'platform', location: 'Body', type: 'string', required: true, description: '自定义种类标识，1-40 位字母数字、下划线或连字符' },
      { name: 'category', location: 'Body', type: 'string', required: false, description: '邮箱分类，默认 safe；空字符串表示不限分类' },
      { name: 'ttl_seconds', location: 'Body', type: 'number', required: false, description: '回退生成临时邮箱的有效期，60-86400 秒；默认 1800 秒，到期停用并进入失效邮箱池' },
      { name: 'lease_seconds', location: 'Body', type: 'number', required: false, description: '租约秒数，范围 30-86400，默认 1800' },
      { name: 'request_id', location: 'Body', type: 'string', required: false, description: '调用方幂等追踪号' },
    ],
    requestExample: `{
  "platform": "service-a",
  "category": "safe",
  "lease_seconds": 1800,
  "ttl_seconds": 1800,
  "request_id": "register-task-001"
}`,
    responseExample: `{
  "ok": true,
  "data": {
    "email": "user@outlook.com",
    "mailbox": {
      "email": "user@outlook.com",
      "password": "MAIL_PASSWORD",
      "leaseToken": "LEASE_TOKEN",
      "expiresAt": 1784476800,
      "platform": "service-a",
      "allocationTier": "imported",
      "mailboxExpiresAt": null
    }
  }
}`,
    statuses: ['200 领取成功', '401 密钥无效', '409 当前无可用邮箱'],
  },
  {
    id: 'icloud-claim-v1', group: 'mailbox', method: 'POST', path: '/api/v1/mailboxes/claim', title: '领取 iCloud 隐私邮箱',
    description: '兼容上游自动取号契约，仅从 provider=icloud 的可用邮箱中领取，并保留 smartMail 原有平台租约隔离。', auth: 'apiKey',
    fields: [
      { name: 'project', location: 'Body', type: 'string', required: false, description: '平台/项目标识，默认 icloud' },
      { name: 'purpose', location: 'Body', type: 'string', required: false, description: '用途说明，默认 register' },
      { name: 'count', location: 'Body', type: 'number', required: false, description: '领取数量，当前只支持 1' },
    ],
    requestExample: `{
  "project": "service-a",
  "purpose": "register",
  "count": 1
}` ,
    responseExample: `{
  "success": true,
  "mailbox": {
    "email": "alias@privaterelay.appleid.com",
    "api_url": "/api/v1/mailboxes/alias%40privaterelay.appleid.com/code",
    "api_active": true,
    "icloud_active": true,
    "status": "used",
    "lease_token": "LEASE_TOKEN"
  }
}` ,
    statuses: ['200 领取成功', '400 count 参数无效', '401 密钥无效', '409 当前无可用 iCloud 邮箱'],
  },
  {
    id: 'mailbox-release', group: 'mailbox', method: 'POST', path: '/api/mailboxes/release', title: '释放邮箱租约',
    description: '注册任务取消或失败时主动释放租约，使邮箱可被同平台再次领取。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Body', type: 'string', required: true, description: '已领取的邮箱地址' },
      { name: 'platform', location: 'Body', type: 'string', required: true, description: '自定义种类标识，1-40 位字母数字、下划线或连字符' },
      { name: 'lease_token', location: 'Body', type: 'string', required: false, description: '领取接口返回的租约 Token' },
    ],
    requestExample: `{
  "email": "user@outlook.com",
  "platform": "service-a",
  "lease_token": "LEASE_TOKEN"
}`,
    responseExample: `{
  "ok": true,
  "data": { "released": true }
}`,
    statuses: ['200 已处理释放请求', '401 密钥无效'],
  },
  {
    id: 'mailbox-code', group: 'mailbox', method: 'GET', path: '/api/mail/code',
    examplePath: '/api/mail/code?email=alias%40privaterelay.appleid.com&keyword=OpenAI&after=2026-08-01T10%3A00%3A00Z&limit=20', title: '读取邮箱验证码',
    description: '从邮箱缓存邮件中查找最近验证码；iCloud 会按配置自动选择 flysms pickup 或原 IMAP 链路实时同步，统一返回 code 和 email 字段。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Query', type: 'string', required: true, description: '目标邮箱地址' },
      { name: 'keyword', location: 'Query', type: 'string', required: false, description: '主题或正文筛选关键词' },
      { name: 'after', location: 'Query', type: 'string', required: false, description: '只接受该 ISO 8601 时间之后的邮件' },
      { name: 'refresh', location: 'Query', type: '0|1', required: false, description: '非 iCloud 邮箱设为 1 时也先同步；iCloud 默认自动同步' },
      { name: 'limit', location: 'Query', type: 'number', required: false, description: '最多检查的最近邮件数，默认 20' },
    ],
    responseExample: `{
  "ok": true,
  "data": "482913",
  "code": "482913",
  "verify_code": "482913",
  "verification_code": "482913",
  "email": "user@outlook.com"
}`,
    statuses: ['200 找到验证码', '401 密钥无效', '404 邮箱或验证码不存在'],
  },
  {
    id: 'icloud-code-v1', group: 'mailbox', method: 'GET', path: '/api/v1/mailboxes/{email}/code',
    examplePath: '/api/v1/mailboxes/alias%40privaterelay.appleid.com/code?project=service-a&after=2026-08-01T10%3A00%3A00Z', title: 'iCloud 标准取码接口',
    description: '兼容 iCloud-Privacy-Mail 单邮箱取码契约。推荐只提供邮箱和完整 pickup 链接，服务端从链接 fragment 自动解析 KEY，并经固定 flysms API 请求头取件；历史配置继续使用真实 iCloud 邮箱 + App 专用密码连接 IMAP。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Path', type: 'string', required: true, description: 'URL 编码后的 iCloud 隐私邮箱地址' },
      { name: 'project', location: 'Query', type: 'string', required: false, description: '可选项目关键词，按邮件内容筛选' },
      { name: 'keyword', location: 'Query', type: 'string', required: false, description: '显式邮件关键词，优先于 project' },
      { name: 'after', location: 'Query', type: 'string', required: false, description: '触发验证码后的 ISO 8601 时间' },
      { name: 'limit', location: 'Query', type: 'number', required: false, description: '同步并检查的最近邮件数，默认 20' },
    ],
    responseExample: `{
  "success": true,
  "email": "alias@privaterelay.appleid.com",
  "code": "482913",
  "subject": "Your OpenAI code is 482913",
  "received_at": "2026-08-01T10:00:15.000Z",
  "message_id": "icloud-pickup:inbox:12345"
}`,
    statuses: ['200 找到验证码', '400 after 参数无效', '401 密钥无效', '404 暂未收到验证码', '502 iCloud pickup/IMAP 同步失败'],
  },
  {
    id: 'mailbox-link', group: 'mailbox', method: 'GET', path: '/api/mail/link',
    examplePath: '/api/mail/link?email=user%40outlook.com&keyword=login&folder=all&limit=20', title: '读取邮箱登录链接',
    description: '从邮箱缓存邮件中提取 Magic Link 登录链接；省略 keyword 时不限种类。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Query', type: 'string', required: true, description: '目标邮箱地址' },
      { name: 'keyword', location: 'Query', type: 'string', required: false, description: '主题、正文或邮件类型筛选关键词，默认不限' },
      { name: 'folder', location: 'Query', type: 'string', required: false, description: '邮件夹，inbox、junkemail 或 all，默认 inbox' },
      { name: 'limit', location: 'Query', type: 'number', required: false, description: '每个邮件夹最多检查的最近邮件数，默认 20' },
    ],
    responseExample: `{
  "ok": true,
  "data": "https://service.example/login?token=EXAMPLE",
  "link": "https://service.example/login?token=EXAMPLE",
  "action_url": "https://service.example/login?token=EXAMPLE",
  "messageType": "login_link",
  "email": "user@outlook.com"
}`,
    statuses: ['200 找到登录链接', '401 密钥无效', '404 邮箱或登录链接不存在'],
  },
  {
    id: 'mailbox-report-code', group: 'mailbox', method: 'POST', path: '/api/mailboxes/report-code', title: '上报取码结果',
    description: '注册机上报取码成功、超时或失败，用于更新邮箱健康状态和错误信息。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Body', type: 'string', required: true, description: '目标邮箱地址' },
      { name: 'result', location: 'Body', type: 'string', required: true, description: 'success、timeout 或 failed' },
      { name: 'detail', location: 'Body', type: 'string', required: false, description: '错误或诊断详情' },
    ],
    requestExample: `{
  "email": "user@outlook.com",
  "result": "success",
  "detail": "verification code received"
}`,
    responseExample: `{
  "ok": true,
  "data": { "updated": true, "health": "healthy" }
}`,
    statuses: ['200 状态已更新', '401 密钥无效'],
  },
  {
    id: 'mailbox-mark-used', group: 'mailbox', method: 'POST', path: '/api/mailboxes/mark-used', title: '标记平台注册成功',
    description: '兼容注册机的快捷成功上报接口，会写入平台账号并释放对应租约。', auth: 'apiKey',
    fields: [
      { name: 'email', location: 'Body', type: 'string', required: true, description: '注册成功使用的邮箱' },
      { name: 'platform', location: 'Body', type: 'string', required: true, description: '自定义种类标识，1-40 位字母数字、下划线或连字符' },
      { name: 'password', location: 'Body', type: 'string', required: false, description: '平台注册账号密码' },
      { name: 'account_id', location: 'Body', type: 'string', required: false, description: '第三方平台账号 ID' },
    ],
    requestExample: `{
  "email": "user@outlook.com",
  "platform": "service-a",
  "password": "ACCOUNT_PASSWORD",
  "account_id": "platform-account-id"
}`,
    responseExample: `{
  "ok": true,
  "data": { "marked": true, "accountId": 12, "mailboxId": 1, "status": "success" }
}`,
    statuses: ['200 上报成功', '400 缺少邮箱', '401 密钥无效'],
  },
  {
    id: 'platform-report', group: 'platform', method: 'POST', path: '/api/platforms/report',
    examplePath: '/api/platforms/report', title: '通用平台注册上报',
    description: '向任意平台写入注册结果。未知 platform 会创建种类；邮箱必须已导入或领取。成功与失败均释放该种类租约，重复上报按邮箱与种类更新。', auth: 'apiKey',
    fields: [
      { name: 'platform', location: 'Body', type: 'string', required: true, description: '自定义种类标识，例如 service-a；也支持 /api/platforms/:slug/accounts/report' },
      { name: 'email', location: 'Body', type: 'string', required: true, description: '注册使用的邮箱，也兼容 bind_email、login_email' },
      { name: 'status', location: 'Body', type: 'string', required: true, description: 'success 或 failed；兼容布尔 success、registration_ok，不接受字符串布尔值' },
      { name: 'password', location: 'Body', type: 'string', required: false, description: '平台注册账号密码' },
      { name: 'tokens', location: 'Body', type: 'object', required: false, description: '平台 Token 对象，将加密保存' },
    ],
    requestExample: `{
  "email": "user@outlook.com",
  "platform": "service-a",
  "status": "success",
  "password": "ACCOUNT_PASSWORD",
  "account_id": "platform-account-id",
  "tokens": { "access_token": "ACCESS_TOKEN" }
}`,
    responseExample: `{
  "ok": true,
  "data": { "accountId": 12, "mailboxId": 1, "status": "success" }
}`,
    statuses: ['201 上报已创建或更新', '400 数据不完整', '401 密钥无效'],
  },
  {
    id: 'compat-domains', group: 'compat', method: 'GET', path: '/api/domains', title: '查询可用邮箱域名',
    description: '通用临时地址兼容接口，返回启用邮箱的域名及数量。', auth: 'apiKey', fields: [],
    responseExample: `{
  "results": [{ "domain": "outlook.com", "isVerified": true, "isPublic": false, "count": 100 }]
}`,
    statuses: ['200 查询成功', '401 密钥无效'],
  },
  {
    id: 'compat-new-address', group: 'compat', method: 'POST', path: '/api/new_address', title: '创建临时邮箱地址',
    description: '按 platform 领取邮箱并返回 jwt 字段携带的租约 Token。后续读取邮件必须使用该 jwt。', auth: 'apiKey',
    fields: [
      { name: 'platform', location: 'Body', type: 'string', required: true, description: '自定义种类标识' },
      { name: 'category', location: 'Body', type: 'string', required: false, description: '邮箱分类，默认 safe' },
      { name: 'domain', location: 'Body', type: 'string', required: false, description: '历史兼容字段，不改变领取顺序' },
    ],
    requestExample: `{
  "platform": "service-a",
  "category": "safe"
}`,
    responseExample: `{
  "address": "user@outlook.com",
  "jwt": "LEASE_TOKEN"
}`,
    statuses: ['201 地址已领取', '401 密钥无效', '409 当前无可用邮箱'],
  },
  {
    id: 'compat-mails', group: 'compat', method: 'GET', path: '/api/mails', examplePath: '/api/mails?limit=20', title: '读取临时邮箱邮件',
    description: '使用 new_address 返回的 jwt 同步并读取收件箱邮件。该租约 Token 只能访问对应邮箱。', auth: 'lease',
    fields: [
      { name: 'Authorization', location: 'Header', type: 'Bearer token', required: true, description: 'Bearer + new_address 返回的 jwt' },
      { name: 'limit', location: 'Query', type: 'number', required: false, description: '返回邮件数量，默认 20，最大 100' },
    ],
    responseExample: `{
  "results": [{
    "msgid": "message-id",
    "subject": "Your verification code",
    "text": "Your code is 482913",
    "code": "482913"
  }]
}`,
    statuses: ['200 邮件读取成功', '401 租约无效或已过期'],
  },
  {
    id: 'compat-mail-detail', group: 'compat', method: 'GET', path: '/api/mail/:messageId',
    examplePath: '/api/mail/message-id', title: '读取单封邮件',
    description: '使用邮箱租约 Token 按消息 ID 读取单封邮件的正文与验证码。', auth: 'lease',
    fields: [
      { name: 'messageId', location: 'Path', type: 'string', required: true, description: '邮件列表返回的 msgid' },
      { name: 'Authorization', location: 'Header', type: 'Bearer token', required: true, description: 'Bearer + 邮箱租约 Token' },
    ],
    responseExample: `{
  "id": "message-id",
  "sender": "noreply@example.com",
  "subject": "Your verification code",
  "text": "Your code is 482913",
  "code": "482913",
  "html": []
}`,
    statuses: ['200 邮件读取成功', '401 租约无效或已过期', '404 邮件不存在'],
  },
];

const authLabels: Record<AuthMode, string> = {
  public: '公开接口',
  apiKey: 'API Key',
  admin: '管理员密钥',
  lease: '租约 Token',
};

/**
 * 功能：生成适用于 Windows PowerShell 5.1+ 的接口调用示例。
 * 参数：endpoint（接口定义）；baseUrl（当前 smartMail 服务地址）。
 */
function buildPowerShellSnippet(endpoint: ApiEndpoint, baseUrl: string): string {
  const lines = [`$baseUrl = '${baseUrl}'`];
  if (endpoint.auth === 'apiKey') lines.push("$headers = @{ Authorization = 'Bearer YOUR_API_KEY' }");
  if (endpoint.auth === 'admin') lines.push("$headers = @{ Authorization = 'Bearer YOUR_ADMIN_KEY' }");
  if (endpoint.auth === 'lease') lines.push("$headers = @{ Authorization = 'Bearer YOUR_LEASE_TOKEN' }");
  if (endpoint.requestExample) lines.push(`$body = @'\n${endpoint.requestExample}\n'@`);
  const headers = endpoint.auth === 'public' ? '' : ' -Headers $headers';
  const body = endpoint.requestExample ? " -ContentType 'application/json' -Body $body" : '';
  lines.push(`Invoke-RestMethod -Method ${endpoint.method} -Uri "$baseUrl${endpoint.examplePath || endpoint.path}"${headers}${body}`);
  return lines.join('\n\n');
}

/**
 * 功能：生成跨平台 cURL 接口调用示例。
 * 参数：endpoint（接口定义）；baseUrl（当前 smartMail 服务地址）。
 */
function buildCurlSnippet(endpoint: ApiEndpoint, baseUrl: string): string {
  const parts = [`curl -X ${endpoint.method} '${baseUrl}${endpoint.examplePath || endpoint.path}'`];
  if (endpoint.auth === 'apiKey') parts.push("  -H 'Authorization: Bearer YOUR_API_KEY'");
  if (endpoint.auth === 'admin') parts.push("  -H 'Authorization: Bearer YOUR_ADMIN_KEY'");
  if (endpoint.auth === 'lease') parts.push("  -H 'Authorization: Bearer YOUR_LEASE_TOKEN'");
  if (endpoint.requestExample) {
    parts.push("  -H 'Content-Type: application/json'");
    parts.push(`  --data '${endpoint.requestExample.replace(/\n/g, '')}'`);
  }
  return parts.join(' \\\n');
}

/**
 * 功能：渲染供第三方程序接入的 smartMail API 文档与代码示例。
 * 参数：notify（全局通知回调）。
 */
export function ApiDocsPanel({ notify }: { notify: (message: string, type?: 'success' | 'error') => void }) {
  const [group, setGroup] = useState<'all' | ApiGroup>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('mailbox-reserve');
  const [language, setLanguage] = useState<SnippetLanguage>('powershell');
  const baseUrl = window.location.origin;

  const visibleEndpoints = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return endpoints.filter((endpoint) => (group === 'all' || endpoint.group === group)
      && (!normalized || `${endpoint.method} ${endpoint.path} ${endpoint.title} ${endpoint.description}`.toLowerCase().includes(normalized)));
  }, [group, query]);
  const selected = visibleEndpoints.find((endpoint) => endpoint.id === selectedId) || visibleEndpoints[0] || null;
  const snippet = useMemo(() => selected
    ? language === 'powershell' ? buildPowerShellSnippet(selected, baseUrl) : buildCurlSnippet(selected, baseUrl)
    : '', [selected, language, baseUrl]);

  /** 功能：复制文档文本到系统剪贴板；参数：value（待复制内容）；label（通知名称）。 */
  const copy = async (value: string, label: string): Promise<void> => {
    try { await navigator.clipboard.writeText(value); notify(`${label}已复制`); }
    catch { notify('复制失败，请手动选择文本', 'error'); }
  };

  return (
    <div className="policy-panel api-docs-panel">
      <div className="panel-title api-docs-title">
        <div><span>EXTERNAL API / V2.0</span><h2>smartMail API 文档</h2><p>面向注册机、自动化任务和第三方平台的推荐调用接口；文档与当前服务实现同步。</p></div>
        <span className="api-docs-version"><CheckCircle2 size={14} />POSTGRESQL READY</span>
      </div>

      <section className="api-docs-proof" aria-label="API 接入概要">
        <div><span>BASE URL</span><strong>{baseUrl}</strong><button onClick={() => void copy(baseUrl, '服务地址')} aria-label="复制服务地址"><Clipboard size={14} />复制</button></div>
        <div><span>AUTHENTICATION</span><strong>Bearer / X-API-Key</strong><small>第三方接入请使用独立 API Key，避免分发服务器管理密钥。</small></div>
        <div><span>RATE LIMIT</span><strong>600 req / min</strong><small>作用于每个来源 IP 的 /api 请求。</small></div>
        <div><span>RESPONSE</span><strong>JSON / UTF-8</strong><small>管理接口统一返回 ok、message、data。</small></div>
      </section>

      <div className="api-docs-toolbar">
        <label className="api-docs-search"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索端点、路径或用途" aria-label="搜索 API 文档" /></label>
        <div className="api-docs-filters" aria-label="API 分类筛选">
          {groupOptions.map((option) => <button key={option.key} className={group === option.key ? 'active' : ''} onClick={() => setGroup(option.key)}>{option.label}</button>)}
        </div>
      </div>

      <div className="api-docs-layout">
        <nav className="api-endpoint-index" aria-label="API 端点列表">
          <div className="api-index-head"><BookOpen size={14} /><span>{visibleEndpoints.length} ENDPOINTS</span></div>
          {visibleEndpoints.map((endpoint) => (
            <button key={endpoint.id} className={selected?.id === endpoint.id ? 'active' : ''} onClick={() => setSelectedId(endpoint.id)}>
              <span className={`api-method method-${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
              <span><strong>{endpoint.title}</strong><code>{endpoint.path}</code></span>
            </button>
          ))}
          {!visibleEndpoints.length ? <div className="api-index-empty">没有匹配的 API 端点</div> : null}
        </nav>

        {selected ? <article className="api-endpoint-detail">
          <header className="api-detail-head">
            <div className="api-detail-meta"><span className={`api-method method-${selected.method.toLowerCase()}`}>{selected.method}</span><code>{selected.path}</code><span className={`api-auth auth-${selected.auth}`}><ShieldCheck size={13} />{authLabels[selected.auth]}</span></div>
            <h3>{selected.title}</h3>
            <p>{selected.description}</p>
          </header>

          <section className="api-doc-section">
            <div className="api-section-title"><span>01</span><div><strong>请求参数</strong><small>PARAMETERS</small></div></div>
            {selected.fields.length ? <div className="api-field-table" role="table" aria-label={`${selected.title} 请求参数`}>
              <div className="api-field-head" role="row"><span>字段</span><span>位置 / 类型</span><span>说明</span></div>
              {selected.fields.map((field) => <div className="api-field-row" role="row" key={`${field.location}-${field.name}`}><code>{field.name}</code><span><small>{field.location}</small><code>{field.type}</code></span><p>{field.description}<em>{field.required ? '必填' : '可选'}</em></p></div>)}
            </div> : <div className="api-no-params">此端点不需要请求参数。</div>}
          </section>

          <section className="api-doc-section">
            <div className="api-section-title"><span>02</span><div><strong>调用示例</strong><small>REQUEST EXAMPLE</small></div></div>
            <div className="api-code-window">
              <div className="api-code-toolbar"><div className="api-language-tabs"><button className={language === 'powershell' ? 'active' : ''} onClick={() => setLanguage('powershell')}><Terminal size={13} />PowerShell</button><button className={language === 'curl' ? 'active' : ''} onClick={() => setLanguage('curl')}><Code2 size={13} />cURL</button></div><button onClick={() => void copy(snippet, '调用示例')}><Clipboard size={13} />复制</button></div>
              <pre><code>{snippet}</code></pre>
            </div>
          </section>

          <section className="api-doc-section">
            <div className="api-section-title"><span>03</span><div><strong>响应示例</strong><small>RESPONSE EXAMPLE</small></div></div>
            <div className="api-response-head"><span>application/json</span><button onClick={() => void copy(selected.responseExample, '响应示例')}><Clipboard size={13} />复制</button></div>
            <pre className="api-response-code"><code>{selected.responseExample}</code></pre>
            <div className="api-status-list">{selected.statuses.map((status) => <span key={status}>{status}</span>)}</div>
          </section>
        </article> : <div className="api-detail-empty"><Search size={20} /><span>调整搜索条件以查看 API 详情</span></div>}
      </div>

      <footer className="api-docs-note"><ShieldCheck size={15} /><p><strong>鉴权建议</strong>：在“权限密钥”中为每个外部程序创建独立密钥；新密钥会加密保存，创建后可随时复制，历史不可恢复密钥需重新创建。请求头优先使用 <code>Authorization: Bearer YOUR_API_KEY</code>，也支持 <code>X-API-Key</code>；URL 查询参数仅为旧注册机兼容保留。iCloud pickup 的 KEY 只应保存在加密字段并通过上游 Authorization 请求头发送，禁止写入日志；后台定时自检不会触发 iCloud。</p></footer>
    </div>
  );
}

